import { createContext, ReactNode, useContext } from 'react';
import { useScreenCaptureDetection, CaptureType } from '@/hooks/useScreenCaptureDetection';

interface RecordingProtectionContextType {
  isRecordingDetected: boolean;
  captureType: CaptureType | null;
  lastDetectedAt: Date | null;
}

const RecordingProtectionContext = createContext<RecordingProtectionContextType | null>(null);

/**
 * Provider that wraps video chat components to detect and respond to screen recording.
 *
 * When recording is detected:
 * - isRecordingDetected becomes true
 * - Video components can use this to show blackout overlay
 * - Video automatically restores when capture stops
 */
export function RecordingProtectionProvider({ children }: { children: ReactNode }) {
  const {
    isScreenBeingCaptured,
    captureType,
    lastDetectedAt
  } = useScreenCaptureDetection();

  return (
    <RecordingProtectionContext.Provider
      value={{
        isRecordingDetected: isScreenBeingCaptured,
        captureType,
        lastDetectedAt
      }}
    >
      {children}
    </RecordingProtectionContext.Provider>
  );
}

/**
 * Hook to access recording protection state.
 * Must be used within RecordingProtectionProvider.
 */
export function useRecordingProtection() {
  const context = useContext(RecordingProtectionContext);
  if (!context) {
    throw new Error('useRecordingProtection must be used within a RecordingProtectionProvider');
  }
  return context;
}

/**
 * Hook to optionally access recording protection state.
 * Returns null if not within RecordingProtectionProvider.
 * Useful for components that may or may not have protection enabled.
 */
export function useRecordingProtectionOptional() {
  return useContext(RecordingProtectionContext);
}
